<template lang="pug">
  .record-payment
    y-dialog(fill-height fluid v-model="showModal" scrollable :fullscreen="$vuetify.breakpoint.xsOnly" transition="fade-transition" persistent max-width="600px")
      v-card(min-height="80vh")
        v-card-title.px-5(:style="`background-color: ${$vuetify.theme.themes[$theme].primary500}`")
          v-row(no-gutters align="center")
            v-col.shrink
              v-btn.ml-n3(text icon @click="showModal = !showModal" height)
                eva-icon(name="arrow-back-outline" :fill="$vuetify.theme.themes[$theme].basic100" width="24" height="24")
            v-col.ml-3
              p.mb-0.h6.basic100--text Record Payment
        
        v-card-text.px-0.pb-0
          v-form(ref="form")

            .payment-date.px-6.mt-6
              .label.basic600--text.text-uppercase Date
              v-dialog(v-model='form.date.show' width='290px')
                template(v-slot:activator='{ on }')
                  v-text-field.mt-2(:value="form.date.value" placeholder="Select date" outlined dense background-color="basic200" readonly v-on='on' :rules="rules.is_selected")
                    template(v-slot:append)
                      eva-icon(name="chevron-down-outline" width="24" height="24" :fill="$vuetify.theme.themes[$theme].basic700")
                v-date-picker(v-model='form.paymentDate' color="primary" no-title @change="saveDate()")
            
            .payment-method.px-6
              .label.basic600--text.text-uppercase Payment Method
              v-row.px-3.mb-n2.mt-2
                v-radio-group.mt-2(v-model='form.paymentMethod' row dense)
                  v-radio.mb-4(v-for="(method, index) in paymentMethods" :value="method.name" style="width:100%" :key="method.id")
                    template(v-slot:label)
                      div
                        .p1.textColorDefault--text {{ method.name }}
              
            .payment-amount.px-6
              .label.basic600--text.text-uppercase Amount paid
              v-text-field.mt-2(v-model='form.paymentAmount' :placeholder="$t('price.placeholder')" background-color="basic200" height="40" outlined dense type="number" :rules="priceRules" :prefix="$currency.symbol")
            
            .outstanding-amount.px-6
              div.d-flex
                .s1.primary500--text Outstanding balance
                v-spacer
                .p1.primary500--text.my-auto {{ outstandingAmount | displayPrice }}
            
            .document.px-6.pb-6
              v-row.mt-4(no-gutters align="center")
                v-col.shrink
                  v-img.mr-4(:src="form.documentImage.url ? form.documentImage.url : require('@/assets/image_placeholder.png')" width="95px" aspect-ratio="1")
                v-col
                  div.file-layout
                    v-btn.button-AA-tiny.text-uppercase(color="textColorAccent" outlined x-small height="24" :style="{'background-color': $vuetify.theme.themes[$theme].infoT08}") upload image
                    input.input-layout(type="file" @change="onFileChange" accept="image/*")
                  div.c1.basic600--text.mt-4 Upload photo of your
                    span(v-if="form.paymentStatus == 'paid'")  receipt
                    span(v-else)  invoice
        
        v-card-actions
          v-spacer
          v-btn(color='white', text @click='showModal = false')
            .button-Aa-medium.primary400--text Cancel
          v-btn.mr-2(color='info500' depressed @click="clickSave()" :loading="isLoading")
            .button-AA-medium.basic100--text {{ $t('save') }}
        

</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { DateTime } from 'luxon'
import { resizeImage } from '@/utils'

export default {
  name: 'RecordPaymentDialog',
  props: ['open', 'parentExpense'],
  data: () => ({
    isFetching: false,
    form: { 
      paymentStatus: 'paid',
      paymentMethod: null,
      paymentDate: null,
      paymentAmount: 0,
      date: {
        show: false,
        value: null
      },
      documentImage: {
        url: '',
        file: null,
      }
    },
    paymentMethods: [],
    isLoading: false,
    rules: {
      is_selected: [(v) => v !== null || 'This field is required'],
      has_quantity: [(v) => !!v || 'This field is required', (v) => v > 0|| 'This field is required']
    }
  }),
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    showModal: {
      get() {
        return this.open
      },
      set(newValue) {
        this.$emit('update:open', newValue)
      }
    },
    expense: {
      get() {
        return this.parentExpense
      },
      set(newValue) {
        this.$emit('update:parentExpense', newValue)
      }
    },
    amountDue () {
      return this.expense.amount_remaining
    },
    outstandingAmount () {
      if (this.form.paymentAmount) {
        const total = this.expense.amount_remaining - this.form.paymentAmount*this.$currency.multiplier
        return total >= 0 ? total : 0
      }
      return this.expense.amount_remaining
    },
    priceRules () {
      return [
        v => !!v || 'Amount is required',
        v => v != 0|| 'Amount is required',
        v => v <= this.amountDue/this.$currency.multiplier || `Amount should not be above ${this.$currency.symbol} ${(parseFloat(this.amountDue/this.$currency.multiplier)).toFixed(this.$currency.exponent)}`,
      ]
    }
  },
  mounted () {
    this.form.paymentDate = DateTime.local().toFormat('yyyy-MM-dd')
    this.form.date.value = this.formatDate(this.form.paymentDate)
    this.getPaymentMethods()
    this.form.paymentAmount = this.expense.amount_remaining/this.$currency.multiplier
  },
  methods: {
    ...mapActions([
      'showMessage'
    ]),
    getPaymentMethods () {
      this.isFetching = true
      this.$axios.get(`/books/${this.company.slug}/payment_methods`).then((response) => {
        this.isFetching = false
        this.paymentMethods = [...response.data.results]
        this.form.paymentMethod = response.data.results.filter(method => method.name.toLowerCase() === 'bank transfer')[0]
        if (!this.form.paymentMethod) {
          this.form.paymentMethod = this.paymentMethods[0].name
        } else {
          this.form.paymentMethod = this.form.paymentMethod.name
        }
      }).catch(() => {})
    },
    saveDate () {
      this.form.date.show = false
      this.form.date.value = this.formatDate(this.form.paymentDate)
    },
    formatDate (date) {
      if (!date) {
        return null
      }
      const [year, month, day] = date.split('-')
      return `${day}/${month}/${year}`
    },
    clickSave() {
      if (!this.$refs.form.validate()) {
        return
      }
      this.isLoading = true
      const params = {
        paid_date: this.form.paymentDate,
        payment_method: this.form.paymentMethod,
        amount: (parseFloat(this.form.paymentAmount)).toFixed(this.$currency.exponent).replace('.', '')
      }
      this.$axios.post(`/books/${this.company.slug}/expenses/${this.expense.id}/payments/`, params)
        .then(resp => {
          this.$emit('payment-added', resp.data)
          this.$axios.get(`/books/${this.company.slug}/expenses/${this.expense.id}/?expand=vendor,category,items`)
            .then(resp => {
              this.expense = resp.data
              if (this.form.documentImage.file) {
                var params = new FormData()
                const docType = params.amount === this.expense.amount_remaining? 'receipt' : 'invoice'
                params.set('doc_type', docType)
                params.set('file', this.form.documentImage.file)
                this.$axios.post(`/books/${this.company.slug}/expenses/${this.expense.id}/documents?doc_type=${docType}`, params)
                  .then(resp => {
                    this.$emit('document-added', resp.data)
                    this.showMessage(['success', this.$t('success_message.expense_recorded_successfully')])
                    this.showModal = false
                  }).catch(() => {})
              } else {
                this.showMessage(['success', this.$t('success_message.expense_recorded_successfully')])
                this.showModal = false
              }
            }).catch(() => {})
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          this.isLoading = false
        })
    },
    getExpense () {
    },
    async onFileChange (e) {
      const files = e.target.files || e.dataTransfer.files
      if (!files.length) { return }
      const resizedImage = await resizeImage(files[0], 'expense_image.jpeg')
      this.form.documentImage.url = resizedImage.dataURL
      this.form.documentImage.file = resizedImage.file
    }
  }
}
</script>

<style scoped>
.file-layout {
  position: relative;
  cursor: pointer;
}
.input-layout {
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  opacity: 0;
}
div >>> .v-list-item--active{
  background-color: var(--v-info500) !important;
  color:white !important;
}
.v-text-field--outlined >>> fieldset {
  border-color: var(--v-primary200);
}
.error--text >>> fieldset {
  border-color: var(--v-danger);
}
</style>

<i18n>
{
  "en": {
    "price": {
      "label": "Amount",
      "placeholder": "0"
    },
    "cancel": "Cancel",
    "save": "Save"
  }
}
</i18n>
